import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../../components/shared/Layout/Layout';
import Button from '../../../components/shared/Button/Button';
import QRDisplay from '../../../components/shared/QRDisplay/QRDisplay';
import {
  ConfirmedContainer,
  SuccessHeader,
  SuccessIcon,
  ReservationCard,
  ReservationDetails,
  QRSection,
  ImportantNotes,
  ActionButtons,
  ShareSection,
} from './BookingConfirmed.styles';

const BookingConfirmed = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);

  useEffect(() => {
    if (location.state && location.state.reservationData) {
      setReservation(location.state.reservationData);
    } else {
      navigate('/');
    }
  }, [location.state, navigate]);

  if (!reservation) {
    return (
      <Layout>
        <div className="loading">Cargando confirmación...</div>
      </Layout>
    );
  }

  const formattedDate = reservation.date
    ? new Date(`${reservation.date}T12:00:00`).toLocaleDateString()
    : '';

  const handleWhatsApp = () => {
    const message = `¡Mi reserva en BokerHub está confirmada! 🎉

📍 ${reservation.activityName || 'Actividad'}
📅 ${formattedDate}
🕐 ${reservation.time}
👥 ${reservation.quantity} persona(s)

ID: ${reservation.id}`;

    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`);
  };

  return (
    <Layout>
      <ConfirmedContainer>
        <SuccessHeader>
          <SuccessIcon>✓</SuccessIcon>
          <h1>¡Reserva confirmada!</h1>
          <p>Tu pago está retenido de forma segura hasta que uses tu código QR</p>
        </SuccessHeader>

        <QRSection>
          <h2>Tu código QR</h2>
          <p>Preséntalo en la entrada el día de tu actividad</p>

          <QRDisplay
            value={reservation.qrCode}
            size={200}
            reservationId={reservation.id}
          />
        </QRSection>

        <ReservationCard>
          <h2>Detalles de tu reserva</h2>
          <ReservationDetails>
            <div className="detail-group">
              <div className="detail-row">
                <span>ID de reserva</span>
                <span className="mono">{reservation.id}</span>
              </div>
              <div className="detail-row">
                <span>Actividad</span>
                <span>{reservation.activityName || 'Actividad'}</span>
              </div>
              {reservation.activityLocation && (
                <div className="detail-row">
                  <span>Ubicación</span>
                  <span>{reservation.activityLocation}</span>
                </div>
              )}
              <div className="detail-row">
                <span>Fecha</span>
                <span>{formattedDate}</span>
              </div>
              <div className="detail-row">
                <span>Hora</span>
                <span>{reservation.time}</span>
              </div>
              <div className="detail-row">
                <span>Personas</span>
                <span>{reservation.quantity}</span>
              </div>
              <div className="detail-row total">
                <span>Total pagado</span>
                <span>${(reservation.finalAmount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
              </div>
            </div>

            <div className="customer-info">
              <h3>Titular de la reserva</h3>
              <p><strong>{reservation.customer?.name}</strong></p>
              <p>{reservation.customer?.email}</p>
              {reservation.customer?.phone && <p>{reservation.customer.phone}</p>}
            </div>
          </ReservationDetails>
        </ReservationCard>

        <ImportantNotes>
          <h3>Información importante</h3>
          <ul>
            <li><strong>Pago seguro:</strong> tu dinero queda retenido hasta que la empresa escanee tu QR en el lugar.</li>
            <li><strong>Reembolso automático:</strong> si la actividad no se realiza, se te devuelve el total.</li>
            <li><strong>Llegada:</strong> preséntate 15 minutos antes de tu hora programada.</li>
            <li><strong>Código único:</strong> el QR es válido solo para esta fecha y hora.</li>
            <li><strong>Cancelación:</strong> hasta 24 horas antes, con reembolso completo desde tu perfil.</li>
          </ul>
        </ImportantNotes>

        <ShareSection>
          <h3>Comparte tu plan</h3>
          <div className="share-buttons">
            <button onClick={handleWhatsApp} className="whatsapp">
              WhatsApp
            </button>
            {navigator.share && (
              <button
                onClick={() =>
                  navigator.share({
                    title: 'Mi reserva en BokerHub',
                    text: `¡Reservé ${reservation.activityName || 'una actividad'} en BokerHub!`,
                  })
                }
              >
                Compartir
              </button>
            )}
          </div>
        </ShareSection>

        <ActionButtons>
          <Button onClick={() => navigate('/')}>Volver al inicio</Button>
          <Button variant="secondary" onClick={() => navigate('/search')}>
            Buscar más actividades
          </Button>
        </ActionButtons>
      </ConfirmedContainer>
    </Layout>
  );
};

export default BookingConfirmed;
